import { useState } from 'react'

import { api } from '../api/client'
import type { GateDecision, GateFinding, GatePlan, GatePolicyIn, GateSetResult, PipelineGateInfo, VexIn, VexRow } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'
import { conseguenza, Finestra } from './ModificheGitlab'
import { scaricaJson } from './Sbom'
import { BadgeGravita, etichettaFonte, LinkEsterno, quando } from './SoftwareProdotto'

/**
 * Software prodotto → the security gate of a project and the VEX assessments
 * it reads. The gate: a rule (warn or block; on what; what if CENSO cannot
 * decide; which sources count), a token that only asks for this project's
 * verdicts, the verdicts with their report. A verdict that blocks shows what
 * blocks, and «Valuta (VEX)» writes the assessment that — expiring — takes a
 * finding out. See pipeline_gate_service and vex_service.
 */

// ------------------------------------------------------------- labels

export function etichettaEsito(o: string): string {
  return ({ pass: t('superato'), warn: t('avviso'), fail: t('bloccato'), pending: t('in valutazione') } as Record<string, string>)[o] ?? o
}

export function BadgeEsito({ d }: { d: Pick<GateDecision, 'status' | 'outcome' | 'error'> }) {
  const esito = d.status === 'pending' ? 'pending' : d.outcome
  const tono = ({ pass: 'badge--ok', warn: 'badge--warn', fail: 'badge--error', pending: 'badge--info' } as Record<string, string>)[esito] ?? 'badge--muted'
  return (
    <span className={`badge ${tono}`} title={d.error ? motivoErrore(d.error) : undefined}>
      {etichettaEsito(esito)}{d.error ? ` · ${t('non deciso')}` : ''}
    </span>
  )
}

function motivoErrore(e: string): string {
  return ({
    no_sbom: t('nessun job della pipeline ha lasciato un SBOM CycloneDX'),
    sbom_job_failed: t('il job censo-sbom non è riuscito'),
    sbom_unreadable: t('l’SBOM della pipeline non si legge'),
    sha_mismatch: t('la pipeline non è sul commit indicato dal job'),
    pipeline_not_found: t('pipeline non trovata nel progetto'),
    source_unavailable: t('GitLab non raggiungibile in quel momento'),
    timeout: t('la valutazione non è finita in tempo'),
    internal: t('errore interno di CENSO'),
  } as Record<string, string>)[e] ?? e
}

const MODI: Array<[GatePolicyIn['mode'], () => string, () => string]> = [
  ['warn', () => t('Avvisa'), () => t('Il job diventa arancione e la pipeline prosegue: il modo con cui cominciare.')],
  ['block', () => t('Blocca'), () => t('Il job fallisce; con «Pipelines must succeed» il merge si ferma.')],
]
const SOGLIE: Array<[GatePolicyIn['block_on'], () => string]> = [
  ['exploited', () => t('Vulnerabilità sfruttate attivamente (CISA KEV)')],
  ['critical', () => t('Sfruttate attivamente e critiche')],
  ['high', () => t('Sfruttate attivamente, critiche e alte')],
]
const SU_ERRORE: Array<[GatePolicyIn['on_error'], () => string]> = [
  ['warn', () => t('Avvisa (consigliato)')], ['pass', () => t('Lascia passare')], ['fail', () => t('Blocca')],
]
const FONTI_ESTERNE = ['xray', 'nexus_iq', 'gitlab']

function etichettaSoglia(s: string): string {
  return SOGLIE.find(([k]) => k === s)?.[1]() ?? s
}

/** CENSO's own severities are Italian words in the gate's findings. */
function gravita(s: string): string {
  return ({ critica: 'critical', alta: 'high', media: 'medium', bassa: 'low' } as Record<string, string>)[s] ?? s
}

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(localeCorrente()) : '—')

// --------------------------------------------------------- gate dialog

function FinestraCancello({ progetto, attuale, onChiudi, onFatto }: {
  progetto: { id: string; path: string }
  attuale: PipelineGateInfo | null
  onChiudi: () => void
  onFatto: (r: GateSetResult) => void
}) {
  const [pol, setPol] = useState<GatePolicyIn>({
    mode: attuale?.mode ?? 'warn', block_on: attuale?.block_on ?? 'exploited',
    on_error: attuale?.on_error ?? 'warn', sources: attuale?.sources ?? ['censo'],
  })
  const [piano, setPiano] = useState<GatePlan | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function prepara() {
    setErrore(null)
    setLavoro(true)
    try { setPiano(await api.supplyGatePreview(progetto.id, pol)) } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }

  async function conferma() {
    setErrore(null)
    setLavoro(true)
    try { onFatto(await api.supplyGateSet(progetto.id, pol)) } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }

  const fonte = (f: string, on: boolean) =>
    setPol({ ...pol, sources: on ? [...new Set([...pol.sources, f])] : pol.sources.filter((x) => x !== f) })

  return (
    <Finestra titolo={attuale?.enabled ? t('Regola del cancello di sicurezza') : t('Attiva il cancello di sicurezza')} sotto={progetto.path} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      {!piano ? (
        <>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('A ogni pipeline il job censo-gate chiede a CENSO il verdetto sull’SBOM che quella pipeline ha prodotto. CENSO legge pipeline e SBOM da GitLab con il token di lettura, non si fida di quello che il job gli dice, e tiene conto delle valutazioni VEX in vigore.')}
          </p>
          <fieldset className="field">
            <legend>{t('Che cosa fa il cancello')}</legend>
            {MODI.map(([k, l, h]) => (
              <div className="checkbox" key={k}>
                <input id={`modo-${k}`} type="radio" name="modo" checked={pol.mode === k} onChange={() => setPol({ ...pol, mode: k })} />
                <label htmlFor={`modo-${k}`}>{l()}<small>{h()}</small></label>
              </div>
            ))}
          </fieldset>
          <div className="toolbar" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ minWidth: 260, flex: 1 }}>
              <label htmlFor="soglia">{pol.mode === 'block' ? t('Blocca su') : t('Avvisa su')}</label>
              <select id="soglia" value={pol.block_on} onChange={(e) => setPol({ ...pol, block_on: e.target.value as GatePolicyIn['block_on'] })}>
                {SOGLIE.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
              </select>
            </div>
            <div className="field" style={{ minWidth: 220 }}>
              <label htmlFor="su-errore">{t('Se CENSO non può decidere')}</label>
              <select id="su-errore" value={pol.on_error} onChange={(e) => setPol({ ...pol, on_error: e.target.value as GatePolicyIn['on_error'] })}>
                {SU_ERRORE.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
              </select>
              <div className="field__hint">{t('Nessun SBOM nella pipeline, GitLab o CENSO irraggiungibili: il verdetto dice sempre perché.')}</div>
            </div>
          </div>
          <fieldset className="field">
            <legend>{t('Le fonti che contano')}</legend>
            <div className="checkbox"><input id="fonte-censo" type="checkbox" checked disabled /><label htmlFor="fonte-censo">{t('CENSO (OSV, KEV)')}<small>{t('Sempre: il motore che valuta ogni SBOM.')}</small></label></div>
            {FONTI_ESTERNE.map((f) => (
              <div className="checkbox" key={f}>
                <input id={`fonte-${f}`} type="checkbox" checked={pol.sources.includes(f)} onChange={(e) => fonte(f, e.target.checked)} />
                <label htmlFor={`fonte-${f}`}>{etichettaFonte(f)}<small>{t('Contano i suoi risultati sui componenti di questa build, se la connessione li riporta per questo progetto o per i suoi artefatti.')}</small></label>
              </div>
            ))}
          </fieldset>
          <div className="toolbar"><button type="button" onClick={() => void prepara()} disabled={lavoro}>{lavoro ? t('Preparazione…') : t('Prepara il piano')}</button></div>
        </>
      ) : (
        <>
          <p style={{ marginTop: 0 }}><strong>{t('Niente è ancora stato fatto.')}</strong></p>
          <p>
            {t('Regola')}: <strong>{pol.mode === 'block' ? t('blocca') : t('avvisa')}</strong> · {etichettaSoglia(pol.block_on)}
            {' · '}{t('se CENSO non può decidere')}: {etichettaEsito(pol.on_error)}
          </p>
          {piano.url ? <p className="field__hint">{t('Indirizzo che il job chiama')}: <span className="mono">{piano.url}</span></p>
            : <div className="banner banner--warn">{t('Manca il dominio della console (Amministrazione → Piattaforma → Dominio): il job non saprebbe dove chiamare CENSO.')}</div>}
          <h4 style={{ margin: '12px 0 4px' }}>{t('Conseguenze')}</h4>
          <ul style={{ paddingLeft: 18 }}>{piano.consequences.map((c) => <li key={c}>{conseguenza(c)}</li>)}</ul>
          <div className="toolbar">
            <button type="button" className="secondary" onClick={() => setPiano(null)} disabled={lavoro}>{t('Indietro')}</button>
            <button type="button" onClick={() => void conferma()} disabled={lavoro || !piano.url}>
              {lavoro ? t('In corso…') : attuale?.enabled ? t('Salva la regola') : t('Attiva il cancello')}
            </button>
          </div>
        </>
      )}
    </Finestra>
  )
}

/** The token, once: CENSO keeps only its HMAC. */
function TokenUnaVolta({ token, onChiudi }: { token: string; onChiudi: () => void }) {
  const [copiato, setCopiato] = useState(false)
  return (
    <Finestra titolo={t('Il token del cancello')} onChiudi={onChiudi}>
      <div className="banner banner--warn">{t('Si vede solo adesso: CENSO ne conserva solo l’impronta. Se lo perdi, rigeneralo.')}</div>
      <p>{t('In GitLab: Impostazioni → CI/CD → Variabili → Aggiungi variabile. Chiave CENSO_GATE_TOKEN, visibilità «Mascherata» (o «Mascherata e nascosta»), «Proteggi la variabile» spento, «Espandi il riferimento della variabile» spento.')}</p>
      <pre className="mono" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{token}</pre>
      <div className="toolbar">
        <button type="button" onClick={() => { void navigator.clipboard?.writeText(token); setCopiato(true) }}>{copiato ? t('Copiato') : t('Copia')}</button>
        <button type="button" className="secondary" onClick={onChiudi}>{t('Fatto')}</button>
      </div>
    </Finestra>
  )
}

// ---------------------------------------------------------- decisions

function Voci({ voci, onValuta, scrive }: { voci: GateFinding[]; onValuta?: (v: GateFinding) => void; scrive: boolean }) {
  const colonne: Column<GateFinding>[] = [
    { key: 'component', header: t('Componente'), value: (v) => `${v.component} ${v.version}`,
      render: (v) => <><span className="mono">{v.component}</span> <span className="text-muted">{v.version}</span></> },
    { key: 'vuln', header: t('Vulnerabilità'), value: (v) => [v.vuln_id, ...v.cves].join(' '),
      render: (v) => <span className="mono">{v.cves[0] ?? v.vuln_id}</span> },
    { key: 'severity', header: t('Gravità'), value: (v) => v.severity, render: (v) => (
      <>{v.exploited && <span className="badge badge--error">{t('sfruttata')}</span>} <BadgeGravita g={gravita(v.severity)} /></>) },
    { key: 'fixed', header: t('Corretta in'), value: (v) => v.fixed, mono: true },
    { key: 'source', header: t('Fonte'), value: (v) => etichettaFonte(v.source) },
    { key: 'vex', header: t('VEX'), value: (v) => v.vex?.state ?? '', render: (v) => v.vex
      ? <small>{etichettaStatoVex(v.vex.state)} · {t('scade il {d}', { d: data(v.vex.expires_at) })}</small> : null },
  ]
  if (onValuta) {
    colonne.push({ key: 'azione', header: '', filter: false, sort: false, render: (v) => (
      <button type="button" className="secondary" disabled={!scrive} onClick={() => onValuta(v)}>{t('Valuta (VEX)')}</button>) })
  }
  return <DataTable rows={voci} rowKey={(v, i) => `${v.source}-${v.vuln_id}-${v.component}-${i}`} columns={colonne} paginazione={false}
    conteggio={{ nome: t('vulnerabilità') }} strumenti={false} />
}

function DettaglioDecisione({ d, scrive, onValuta }: { d: GateDecision; scrive: boolean; onValuta: (v: GateFinding) => void }) {
  const f = d.findings ?? {}
  return (
    <div style={{ padding: '8px 4px' }}>
      {(f.blocking ?? []).length > 0 && (
        <>
          <h4 style={{ margin: '0 0 4px' }}>{d.outcome === 'fail' ? t('Bloccano') : t('Da correggere')} ({f.blocking_count})</h4>
          <Voci voci={f.blocking ?? []} onValuta={onValuta} scrive={scrive} />
        </>
      )}
      {(f.excluded ?? []).length > 0 && (
        <>
          <h4 style={{ margin: '12px 0 4px' }}>{t('Escluse da una valutazione VEX')} ({f.excluded_count})</h4>
          <Voci voci={f.excluded ?? []} scrive={scrive} />
        </>
      )}
      <details style={{ marginTop: 8 }}>
        <summary>{t('Il rapporto stampato nel log della pipeline')}</summary>
        <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{d.report}</pre>
      </details>
    </div>
  )
}

export function TabellaDecisioni({ righe, conProgetto = true, aperta, scrive, onValuta }: {
  righe: GateDecision[]
  conProgetto?: boolean
  aperta?: string | null
  scrive: boolean
  onValuta: (d: GateDecision, v: GateFinding) => void
}) {
  const [aperte, setAperte] = useState<Set<string>>(() => new Set(aperta ? [aperta] : []))
  const colonne: Column<GateDecision>[] = [
    { key: 'created_at', header: t('Quando'), type: 'date', value: (d) => d.created_at, render: (d) => quando(d.created_at) },
    { key: 'outcome', header: t('Esito'), value: (d) => etichettaEsito(d.status === 'pending' ? 'pending' : d.outcome), render: (d) => <BadgeEsito d={d} /> },
    ...(conProgetto ? [{ key: 'project', header: t('Progetto'), value: (d: GateDecision) => d.project, mono: true }] : []),
    { key: 'pipeline', header: t('Pipeline'), value: (d) => d.pipeline_id, render: (d) => d.pipeline_url
      ? <LinkEsterno href={d.pipeline_url}>#{d.pipeline_id}</LinkEsterno> : <span className="mono">#{d.pipeline_id}</span> },
    { key: 'ref', header: t('Ramo o MR'), value: (d) => d.ref, render: (d) => <><span className="mono">{d.ref || '—'}</span><br /><small className="mono text-muted">{d.sha.slice(0, 8)}</small></> },
    { key: 'blocking', header: t('Da correggere'), type: 'number', value: (d) => d.findings?.blocking_count ?? 0 },
    { key: 'excluded', header: t('Escluse da VEX'), type: 'number', value: (d) => d.findings?.excluded_count ?? 0 },
    { key: 'coverage', header: t('Copertura'), value: (d) => d.coverage?.components ?? 0, render: (d) => d.error
      ? <small className="text-muted">{motivoErrore(d.error)}</small>
      : <small>{t('{v} di {n} valutati', { v: d.coverage?.evaluated ?? 0, n: d.coverage?.components ?? 0 })}{d.coverage?.unevaluated ? ` · ${t('{n} senza catalogo', { n: d.coverage.unevaluated })}` : ''}</small> },
  ]
  return (
    <DataTable rows={righe} rowKey={(d) => d.id} columns={colonne} initialSort={{ key: 'created_at', dir: 'desc' }}
      conteggio={{ nome: t('verdetti') }} emptyText={t('Nessun verdetto finora: arriva alla prima pipeline con il job censo-gate.')}
      expand={(d) => <DettaglioDecisione d={d} scrive={scrive} onValuta={(v) => onValuta(d, v)} />}
      expanded={(d) => aperte.has(d.id)}
      onRowClick={(d) => setAperte((x) => { const n = new Set(x); if (n.has(d.id)) n.delete(d.id); else n.add(d.id); return n })} />
  )
}

// ----------------------------------------------------- the gate card

export function CardCancello({ progetto, scrive, scriveGitlab, aperta, onAggiungiJob, onValuta }: {
  progetto: { id: string; path: string; merge_requires_pipeline: boolean | null }
  scrive: boolean
  /** Writing in GitLab too (the MR with the job). */
  scriveGitlab: boolean
  aperta?: string | null
  onAggiungiJob: () => void
  onValuta: (v: GateFinding) => void
}) {
  const dati = useAsync(() => api.supplyGate(progetto.id), [progetto.id])
  const [finestra, setFinestra] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const g = dati.data?.gate ?? null
  const attivo = Boolean(g?.enabled)

  const consegnato = (r: GateSetResult) => {
    setFinestra(false)
    if (r.token) setToken(r.token)
    setEsito(r.variable_written ? t('Token scritto nella variabile CI mascherata CENSO_GATE_TOKEN del progetto.')
      : r.token ? t('CENSO non ha potuto scrivere la variabile CI: copiala tu adesso.') : t('Regola salvata.'))
    void dati.reload()
  }

  async function rigenera() {
    setErrore(null)
    if (!window.confirm(t('Il token attuale smette subito di funzionare: le pipeline in corso prenderanno la regola «se CENSO non può decidere». Continuare?'))) return
    try { consegnato(await api.supplyGateToken(progetto.id)) } catch (e) { setErrore(e) }
  }

  async function spegni() {
    setErrore(null)
    if (!window.confirm(t('Il cancello si spegne e il token si revoca: il job censo-gate resta nel file ma non parte più senza la variabile. Continuare?'))) return
    try {
      const r = await api.supplyGateOff(progetto.id)
      setEsito(r.variable_removed ? t('Cancello spento, variabile CI tolta.') : t('Cancello spento. Togli tu la variabile CI CENSO_GATE_TOKEN dal progetto.'))
      void dati.reload()
    } catch (e) { setErrore(e) }
  }

  return (
    <Card title={t('Cancello di sicurezza')}>
      <ErrorBanner error={dati.error ?? errore} />
      {esito && <div className="banner banner--info">{esito}</div>}
      {dati.loading && !dati.data ? <Spinner /> : (
        <>
          {attivo && g ? (
            <>
              <p style={{ marginTop: 0 }}>
                <span className={`badge ${g.mode === 'block' ? 'badge--error' : 'badge--warn'}`}>{g.mode === 'block' ? t('blocca') : t('avvisa')}</span>{' '}
                {etichettaSoglia(g.block_on)} · {t('se CENSO non può decidere')}: <strong>{etichettaEsito(g.on_error)}</strong>
                {' · '}{t('fonti')}: {g.sources.map(etichettaFonte).join(', ')}
              </p>
              <p className="field__hint">
                {g.token.present
                  ? t('Token …{h} creato {quando}', { h: g.token.hint, quando: quando(g.token.created_at) })
                    + (g.token.delivery === 'written' ? ` · ${t('nella variabile CI scritta da CENSO')}` : g.token.delivery === 'manual' ? ` · ${t('da copiare a mano nella variabile CI')}` : '')
                  : t('Nessun token: rigeneralo.')}
                {g.url && <> · <span className="mono">{g.url}</span></>}
              </p>
              {g.mode === 'block' && progetto.merge_requires_pipeline === false && (
                <div className="banner banner--warn">{t('«Pipelines must succeed» è spento in questo progetto: il cancello fa fallire il job ma non ferma il merge.')}</div>
              )}
            </>
          ) : (
            <p style={{ marginTop: 0 }} className="text-muted">
              {t('Il cancello non è attivo. Acceso, ogni pipeline chiede a CENSO un verdetto sul proprio SBOM: le vulnerabilità sfruttate attivamente (o anche le critiche e le alte) diventano un avviso o un blocco, con il rapporto nel log del job.')}
            </p>
          )}
          <div className="toolbar">
            <button type="button" disabled={!scrive} onClick={() => setFinestra(true)}>{attivo ? t('Cambia la regola') : t('Attiva il cancello')}</button>
            {attivo && <button type="button" className="secondary" disabled={!scriveGitlab} onClick={onAggiungiJob}>{t('Aggiungi il job del cancello')}</button>}
            {attivo && <button type="button" className="secondary" disabled={!scrive} onClick={() => void rigenera()}>{t('Rigenera il token')}</button>}
            {attivo && <button type="button" className="secondary" disabled={!scrive} onClick={() => void spegni()}>{t('Spegni il cancello')}</button>}
          </div>
          {(dati.data?.decisions ?? []).length > 0 && (
            <TabellaDecisioni righe={dati.data?.decisions ?? []} conProgetto={false} aperta={aperta} scrive={scrive}
              onValuta={(_d, v) => onValuta(v)} />
          )}
        </>
      )}
      {finestra && <FinestraCancello progetto={progetto} attuale={g} onChiudi={() => setFinestra(false)} onFatto={consegnato} />}
      {token && <TokenUnaVolta token={token} onChiudi={() => setToken(null)} />}
    </Card>
  )
}

// ---------------------------------------------------------------- VEX

const STATI_VEX: Array<[string, () => string]> = [
  ['not_affected', () => t('non interessato')],
  ['false_positive', () => t('falso positivo')],
  ['resolved', () => t('risolta')],
  ['resolved_with_pedigree', () => t('risolta, con la provenienza (pedigree)')],
  ['in_triage', () => t('in analisi')],
  ['exploitable', () => t('sfruttabile: siamo interessati')],
]
export const GIUSTIFICAZIONI: Array<[string, () => string]> = [
  ['code_not_present', () => t('il codice vulnerabile non è presente')],
  ['code_not_reachable', () => t('il codice vulnerabile non è raggiungibile')],
  ['requires_configuration', () => t('serve una configurazione che non usiamo')],
  ['requires_dependency', () => t('serve una dipendenza che non c’è')],
  ['requires_environment', () => t('serve un ambiente che non c’è')],
  ['protected_by_compiler', () => t('protetto dal compilatore')],
  ['protected_at_runtime', () => t('protetto in esecuzione')],
  ['protected_at_perimeter', () => t('protetto al perimetro')],
  ['protected_by_mitigating_control', () => t('protetto da un controllo di mitigazione')],
]
const RISPOSTE: Array<[string, () => string]> = [
  ['update', () => t('aggiornare')], ['rollback', () => t('tornare alla versione precedente')],
  ['workaround_available', () => t('c’è un rimedio provvisorio')], ['will_not_fix', () => t('non si correggerà')],
  ['can_not_fix', () => t('non si può correggere')],
]
const ESCLUDONO = ['not_affected', 'false_positive', 'resolved', 'resolved_with_pedigree']

export function etichettaStatoVex(s: string): string {
  return STATI_VEX.find(([k]) => k === s)?.[1]() ?? s
}

function giorno(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

/** The subject of a new assessment: a finding of a verdict, or of the SBOM. */
export interface SoggettoVex { vuln_id: string; aliases: string[]; ecosystem: string; name: string; purl_base: string; purl?: string; version: string }

export function FinestraVex({ progettoId, prodottoCra, soggetto, esistente, onChiudi, onFatto }: {
  progettoId: string | null
  /** A CRA product: the assessment is the product's (not a project's). */
  prodottoCra?: string
  soggetto?: SoggettoVex
  esistente?: VexRow
  onChiudi: () => void
  onFatto: () => void
}) {
  const oggi = new Date()
  const [stato, setStato] = useState(esistente?.state ?? 'not_affected')
  const [giust, setGiust] = useState(esistente?.justification ?? 'code_not_reachable')
  const [risposte, setRisposte] = useState<string[]>(esistente?.responses ?? [])
  const [dettaglio, setDettaglio] = useState(esistente?.detail ?? '')
  const [scade, setScade] = useState(esistente ? esistente.expires_at.slice(0, 10) : giorno(new Date(oggi.getTime() + 90 * 864e5)))
  const [soloVersione, setSoloVersione] = useState(Boolean(esistente?.version ?? soggetto?.version))
  const [tutti, setTutti] = useState(esistente ? !esistente.project_id : false)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const sog = esistente ?? soggetto
  if (!sog) return null

  async function salva() {
    setErrore(null)
    setLavoro(true)
    const fine = new Date(`${scade}T23:59:59`).toISOString()
    try {
      const campi = { state: stato, justification: stato === 'not_affected' ? giust : '', responses: risposte, detail: dettaglio, expires_at: fine }
      if (esistente) await api.supplyVexUpdate(esistente.id, campi)
      else {
        const corpo: VexIn = { ...campi, project_id: tutti ? null : progettoId, vuln_id: sog!.vuln_id, aliases: sog!.aliases,
          ecosystem: sog!.ecosystem, name: sog!.name, purl_base: sog!.purl_base, purl: 'purl' in sog! ? sog!.purl : undefined,
          version: soloVersione ? sog!.version : '' }
        if (prodottoCra) await api.craProductVex(prodottoCra, { ...corpo, project_id: null })
        else await api.supplyVexCreate(corpo)
      }
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const max = giorno(new Date(oggi.getTime() + 365 * 864e5))
  return (
    <Finestra titolo={esistente ? t('Aggiorna la valutazione VEX') : t('Nuova valutazione VEX')} sotto={`${sog.vuln_id} · ${sog.name}`} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Quello che l’organizzazione dichiara su questa vulnerabilità in questo componente, con gli stati e le giustificazioni di CycloneDX. Scade sempre: alla scadenza il cancello torna a contarla. Ogni modifica resta nello storico.')}
      </p>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ minWidth: 240, flex: 1 }}>
          <label htmlFor="vex-stato">{t('Stato')}</label>
          <select id="vex-stato" value={stato} onChange={(e) => setStato(e.target.value)}>
            {STATI_VEX.map(([k, l]) => <option key={k} value={k}>{l()} ({k})</option>)}
          </select>
          <div className="field__hint">{ESCLUDONO.includes(stato) ? t('Il cancello non la conta finché la valutazione è in vigore.') : t('Il cancello continua a contarla: è un’annotazione.')}</div>
        </div>
        {stato === 'not_affected' && (
          <div className="field" style={{ minWidth: 260, flex: 1 }}>
            <label htmlFor="vex-giust">{t('Perché non siamo interessati')}</label>
            <select id="vex-giust" value={giust} onChange={(e) => setGiust(e.target.value)}>
              {GIUSTIFICAZIONI.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
            </select>
          </div>
        )}
        <div className="field" style={{ minWidth: 160 }}>
          <label htmlFor="vex-scade">{t('Scade il')}</label>
          <input id="vex-scade" type="date" value={scade} min={giorno(oggi)} max={max} onChange={(e) => setScade(e.target.value)} />
          <div className="field__hint">{t('Al massimo un anno.')}</div>
        </div>
      </div>
      <div className="field">
        <label htmlFor="vex-dettaglio">{t('Spiegazione')}</label>
        <textarea id="vex-dettaglio" rows={3} value={dettaglio} maxLength={4000} onChange={(e) => setDettaglio(e.target.value)}
          placeholder={t('Per esempio: il lookup JNDI è disattivato e la classe JndiLookup è tolta dal jar (verificato nel build).')} />
        <div className="field__hint">{t('Chi legge la valutazione — un collega, un cliente, un’autorità — deve poterla verificare.')}</div>
      </div>
      <fieldset className="field">
        <legend>{t('Risposta')}</legend>
        {RISPOSTE.map(([k, l]) => (
          <div className="checkbox" key={k}>
            <input id={`vex-r-${k}`} type="checkbox" checked={risposte.includes(k)}
              onChange={(e) => setRisposte(e.target.checked ? [...risposte, k] : risposte.filter((x) => x !== k))} />
            <label htmlFor={`vex-r-${k}`}>{l()}</label>
          </div>
        ))}
      </fieldset>
      {!esistente && (
        <>
          {sog.version && (
            <div className="checkbox">
              <input id="vex-versione" type="checkbox" checked={soloVersione} onChange={(e) => setSoloVersione(e.target.checked)} />
              <label htmlFor="vex-versione">{t('Solo per la versione {v}', { v: sog.version })}<small>{t('Spenta, vale per ogni versione del componente.')}</small></label>
            </div>
          )}
          {!prodottoCra && <div className="checkbox">
            <input id="vex-tutti" type="checkbox" checked={tutti} onChange={(e) => setTutti(e.target.checked)} />
            <label htmlFor="vex-tutti">{t('Per tutti i progetti del tenant')}<small>{t('Per esempio un falso positivo del catalogo. Spenta, vale solo per questo progetto.')}</small></label>
          </div>}
        </>
      )}
      <div className="toolbar">
        <button type="button" onClick={() => void salva()} disabled={lavoro}>{lavoro ? t('Salvataggio…') : t('Salva la valutazione')}</button>
      </div>
    </Finestra>
  )
}

export function TabellaVex({ righe, scrive, conProgetto = true, onCambiato }: {
  righe: VexRow[]
  scrive: boolean
  conProgetto?: boolean
  onCambiato: () => void
}) {
  const [modifica, setModifica] = useState<VexRow | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function revoca(v: VexRow) {
    if (!window.confirm(t('La valutazione si revoca e resta nello storico: il cancello torna a contare la vulnerabilità. Continuare?'))) return
    try { await api.supplyVexRevoke(v.id); onCambiato() } catch (e) { setErrore(e) }
  }

  const colonne: Column<VexRow>[] = [
    { key: 'vuln_id', header: t('Vulnerabilità'), nowrap: true, value: (v) => [v.vuln_id, ...v.aliases].join(' '), render: (v) => <span className="mono">{v.vuln_id}</span> },
    { key: 'name', header: t('Componente'), value: (v) => `${v.name} ${v.version}`,
      render: (v) => <><span className="mono">{v.name}</span> <small className="text-muted">{v.version || t('ogni versione')}</small></> },
    ...(conProgetto ? [{ key: 'project', header: t('Progetto'), value: (v: VexRow) => (v.product_id ? t('prodotto CRA') : v.project || t('tutti i progetti')) }] : []),
    { key: 'state', header: t('Stato'), value: (v) => etichettaStatoVex(v.state), render: (v) => (
      <><span className={`badge ${v.excludes ? 'badge--ok' : 'badge--info'}`}>{etichettaStatoVex(v.state)}</span>
        {v.justification && <><br /><small>{GIUSTIFICAZIONI.find(([k]) => k === v.justification)?.[1]() ?? v.justification}</small></>}</>) },
    { key: 'expires_at', header: t('Scade'), type: 'date', value: (v) => v.expires_at, render: (v) => (
      v.revoked_at ? <span className="badge badge--muted">{t('revocata')}</span>
        : !v.active ? <span className="badge badge--error">{t('scaduta il {d}', { d: data(v.expires_at) })}</span>
          : <span className={v.expires_soon ? 'badge badge--warn' : undefined}>{data(v.expires_at)}</span>) },
    { key: 'detail', header: t('Spiegazione'), value: (v) => v.detail, render: (v) => <small>{v.detail}</small> },
    { key: 'updated_by', header: t('Di chi'), value: (v) => v.updated_by, render: (v) => <small>{v.updated_by}<br />{quando(v.updated_at)}</small> },
    { key: 'azioni', header: '', filter: false, sort: false, render: (v) => !v.revoked_at && (
      <span style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="secondary" disabled={!scrive} onClick={() => setModifica(v)}>{t('Aggiorna')}</button>{' '}
        <button type="button" className="secondary" disabled={!scrive} onClick={() => void revoca(v)}>{t('Revoca')}</button>
      </span>) },
  ]
  return (
    <>
      <ErrorBanner error={errore} />
      <DataTable rows={righe} rowKey={(v) => v.id} columns={colonne} initialSort={{ key: 'expires_at', dir: 'asc' }}
        conteggio={{ nome: t('valutazioni') }} emptyText={t('Nessuna valutazione VEX.')}
        expand={(v) => v.history.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {v.history.slice().reverse().map((h, i) => (
              <li key={i}><small>{quando(h.at)} · {h.by}: {etichettaStatoVex(h.state)}{h.revoked ? ` (${t('revocata')})` : ''} — {h.detail}</small></li>
            ))}
          </ul>)} />
      {modifica && <FinestraVex progettoId={modifica.project_id} esistente={modifica} onChiudi={() => setModifica(null)}
        onFatto={() => { setModifica(null); onCambiato() }} />}
    </>
  )
}

/** The project's VEX card: its assessments, the exports, a new one from the SBOM. */
export function CardVex({ progetto, vulnerabili, scrive }: {
  progetto: { id: string; path: string }
  vulnerabili: SoggettoVex[]
  scrive: boolean
}) {
  const dati = useAsync(() => api.supplyVex(progetto.id), [progetto.id])
  const [nuova, setNuova] = useState<SoggettoVex | null>(null)
  const [scelta, setScelta] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const chiave = (v: SoggettoVex) => `${v.vuln_id}|${v.name}|${v.version}`
  const base = progetto.path.replace(/[^A-Za-z0-9._-]+/g, '-')

  async function esporta(formato: 'cyclonedx' | 'openvex') {
    setErrore(null)
    try {
      await scaricaJson(api.supplyVexExportPath(progetto.id, formato), `${base}-${formato === 'openvex' ? 'openvex.json' : 'vex.cdx.json'}`)
    } catch (e) { setErrore(e) }
  }

  return (
    <Card title={t('Valutazioni (VEX)')}>
      <ErrorBanner error={dati.error ?? errore} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Le valutazioni in vigore escono anche verso i clienti: CycloneDX VEX o OpenVEX, con gli stessi stati e le stesse spiegazioni.')}
      </p>
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        {vulnerabili.length > 0 && (
          <>
            <div className="field" style={{ minWidth: 280, flex: '0 1 520px' }}>
              <label htmlFor="vex-nuova">{t('Nuova valutazione su')}</label>
              <select id="vex-nuova" value={scelta} onChange={(e) => setScelta(e.target.value)}>
                <option value="">{t('— una vulnerabilità dell’SBOM —')}</option>
                {vulnerabili.map((v) => <option key={chiave(v)} value={chiave(v)}>{v.vuln_id} · {v.name} {v.version}</option>)}
              </select>
            </div>
            <button type="button" disabled={!scrive || !scelta} onClick={() => setNuova(vulnerabili.find((v) => chiave(v) === scelta) ?? null)}>{t('Valuta')}</button>
          </>
        )}
        <button type="button" className="secondary" onClick={() => void esporta('cyclonedx')}>{t('Esporta CycloneDX VEX')}</button>
        <button type="button" className="secondary" onClick={() => void esporta('openvex')}>{t('Esporta OpenVEX')}</button>
      </div>
      {dati.loading && !dati.data ? <Spinner /> : <TabellaVex righe={dati.data ?? []} scrive={scrive} conProgetto={false} onCambiato={() => void dati.reload()} />}
      {nuova && <FinestraVex progettoId={progetto.id} soggetto={nuova} onChiudi={() => setNuova(null)} onFatto={() => { setNuova(null); void dati.reload() }} />}
    </Card>
  )
}
